import z from 'zod';
import { ContactFormValuesSchema } from '../types/formSubmissions';
import {
    type BlockQuestion,
    type ContactBlockConfig,
    type QuestionSchema,
    type TemplateFormFieldUpdate,
} from '../types/templateFormFields';
import {
    getConditionalDescendantNames,
    groupQuestionsWithConditionals,
} from './conditionalQuestions';
import {
    formatContactFormFieldDisplayValue,
    formatIntakeFieldDisplayValue,
    formatIntakeTableDisplayValue,
} from './intakeFieldFormatting';
import { getVisibleQuestions } from './questionVisibility';

/**
 * One person in a `contactInfoSection` answer.
 *
 * - `entryId` is stable for the life of the entry so it can later be linked to contacts, files, etc.
 * - `contact` uses the same keys as the step-0 contact section (`ContactFormValues`).
 * - `answers` holds nested question answers keyed by question `name`, encoded exactly like
 *   top-level form values.
 */
export const ContactBlockEntrySchema = z.object({
    entryId: z.string().min(1),
    contact: ContactFormValuesSchema,
    answers: z.record(z.string(), z.string().nullable()),
});
export type ContactBlockEntry = z.infer<typeof ContactBlockEntrySchema>;

export interface ContactBlockValue {
    entries: ContactBlockEntry[];
}

export const DEFAULT_CONTACT_BLOCK_CONFIG: ContactBlockConfig = {
    entryLabel: 'Contact',
    questions: [],
};

export function getContactBlockConfig(
    schema: Pick<QuestionSchema, 'contactBlock'>,
): ContactBlockConfig {
    const config = { ...DEFAULT_CONTACT_BLOCK_CONFIG, ...schema.contactBlock };
    return { ...config, questions: config.questions ?? [] };
}

export function getContactBlockQuestions(
    schema: Pick<QuestionSchema, 'contactBlock'>,
): BlockQuestion[] {
    return schema.contactBlock?.questions ?? [];
}

export function createEmptyContactBlockEntry(): ContactBlockEntry {
    return {
        entryId: crypto.randomUUID(),
        contact: { firstName: '', lastName: '', email: '', cellPhone: '' },
        answers: {},
    };
}

/** Tolerant parse: malformed JSON or entries are dropped rather than throwing. */
export function parseContactBlockValue(value: unknown): ContactBlockValue {
    if (typeof value !== 'string' || value.trim() === '') {
        return { entries: [] };
    }

    let parsed: unknown;
    try {
        parsed = JSON.parse(value);
    } catch {
        return { entries: [] };
    }

    if (
        !parsed ||
        typeof parsed !== 'object' ||
        !Array.isArray((parsed as ContactBlockValue).entries)
    ) {
        return { entries: [] };
    }

    const entries = (parsed as { entries: unknown[] }).entries.flatMap((entry) => {
        const result = ContactBlockEntrySchema.safeParse(entry);
        return result.success ? [result.data] : [];
    });

    return { entries };
}

export function serializeContactBlockValue(value: ContactBlockValue): string {
    return JSON.stringify(value);
}

/** True when the string is a serialized contact block value (as opposed to e.g. a table answer). */
export function isContactBlockValueString(value: string | null | undefined): boolean {
    if (!value) return false;
    try {
        const parsed = JSON.parse(value);
        return !!parsed && typeof parsed === 'object' && Array.isArray(parsed.entries);
    } catch {
        return false;
    }
}

/**
 * Every block shows at least one entry, even an optional one, so clients can fill it in without
 * adding it first. Returns the same array when nothing is padded.
 */
export function ensureContactBlockEntries(entries: ContactBlockEntry[]): ContactBlockEntry[] {
    return entries.length > 0 ? entries : [createEmptyContactBlockEntry()];
}

/** True when an entry has no contact details and no answers to its visible questions. */
export function isContactBlockEntryBlank(
    entry: ContactBlockEntry,
    questions: readonly BlockQuestion[],
): boolean {
    const hasContactValue = Object.entries(entry.contact).some(
        ([key, value]) => formatContactFormFieldDisplayValue(key, value) !== '',
    );
    if (hasContactValue) return false;

    return getVisibleQuestions(
        questions,
        (question) => entry.answers[question.questionSchema.name],
    ).every((question) => {
        const answer = entry.answers[question.questionSchema.name];
        return question.questionSchema.type === 'table'
            ? formatIntakeTableDisplayValue(answer, question.questionSchema).rows.length === 0
            : formatIntakeFieldDisplayValue(answer, question.questionSchema).trim() === '';
    });
}

/**
 * The entries of a contact block answer that have something filled in. Blank entries, such as
 * the one every block starts with, are not part of the answer.
 */
export function getFilledContactBlockEntries(
    value: unknown,
    schema: Pick<QuestionSchema, 'contactBlock'>,
): ContactBlockEntry[] {
    const questions = getContactBlockQuestions(schema);
    return parseContactBlockValue(value).entries.filter(
        (entry) => !isContactBlockEntryBlank(entry, questions),
    );
}

/** True when a block with this many entries can take another one. */
export function canAddContactBlockEntry(
    entryCount: number,
    config: Pick<ContactBlockConfig, 'maxEntries'>,
): boolean {
    return config.maxEntries === undefined || entryCount < config.maxEntries;
}

/** "Director 2" style title for an entry, or just "Director" when it is the only entry. */
export function getContactBlockEntryTitle(
    entryLabel: string,
    index: number,
    entryCount: number,
): string {
    return entryCount === 1 ? entryLabel : `${entryLabel} ${index + 1}`;
}

/**
 * Converts a question built with the regular question editor into a block question. Placement
 * (form group, order) comes from the block and its `questions` array order, so it is dropped.
 */
export function toBlockQuestion(field: TemplateFormFieldUpdate): BlockQuestion {
    const {
        formGroup: _formGroup,
        formGroupTitle: _formGroupTitle,
        order: _order,
        tags: _tags,
        ...questionSchema
    } = field.questionSchema as QuestionSchema & { formGroup?: number };
    return {
        name: field.name,
        title: field.title,
        subTitle: field.subTitle,
        questionSchema: { ...questionSchema, name: field.name },
        questionValidationSchema: field.questionValidationSchema,
        isVisibleToUser: field.isVisibleToUser,
    };
}

/**
 * Replaces questions with matching names in place and inserts new ones. New conditional
 * questions go directly after their parent's existing group; other new questions are appended.
 */
export function upsertBlockQuestions(
    questions: readonly BlockQuestion[],
    incoming: readonly BlockQuestion[],
): BlockQuestion[] {
    let next = [...questions];
    for (const question of incoming) {
        const existingIndex = next.findIndex((existing) => existing.name === question.name);
        if (existingIndex >= 0) {
            next[existingIndex] = question;
            continue;
        }

        const parentName = question.questionSchema.conditionedBy;
        const parentIndex = parentName
            ? next.findIndex((existing) => existing.name === parentName)
            : -1;
        if (!question.questionSchema.isConditionalQuestion || parentIndex < 0) {
            next.push(question);
            continue;
        }

        const descendantNames = new Set(
            getConditionalDescendantNames(next, next[parentIndex].name),
        );
        let insertAt = parentIndex + 1;
        while (insertAt < next.length && descendantNames.has(next[insertAt].name)) insertAt++;
        next = [...next.slice(0, insertAt), question, ...next.slice(insertAt)];
    }
    return next;
}

/** Moves a top-level block question (with its conditionals) up or down one position. */
export function moveBlockQuestion(
    questions: readonly BlockQuestion[],
    name: string,
    direction: -1 | 1,
): BlockQuestion[] {
    const groups = groupQuestionsWithConditionals(questions);
    const index = groups.findIndex((group) => group.question.name === name);
    const targetIndex = index + direction;
    if (index < 0 || targetIndex < 0 || targetIndex >= groups.length) return [...questions];

    const reordered = [...groups];
    [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];
    return reordered.flatMap((group) => [group.question, ...group.conditionals]);
}
